import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { useCallback, useEffect } from 'react';
import { BlockerFunction, useBlocker } from 'react-router';
import { t } from 'utils/getTranslations';

/**
 * Asks before leaving a section with unsaved changes. Unlike
 * `ConfirmNavigation`, which is bound to one whole form's dirty state and lets a search-only change through, it
 * takes a flag and blocks any other location: a record's tabs, and its `?edit=`, live in the URL.
 */
const SectionUnsavedChangesGuard = ({ when }: { when: boolean }) => {
    const { openModal, closeModal } = useModal();

    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) =>
            when &&
            (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
        [when]
    );
    const blocker = useBlocker(shouldBlock);
    const isBlocked = blocker.state === 'blocked';

    useEffect(() => {
        if (!when) {
            return undefined;
        }
        const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [when]);

    const handleProceed = useCallback(() => {
        closeModal();
        blocker.proceed?.();
    }, [closeModal, blocker]);

    const handleCancel = useCallback(() => {
        closeModal();
        blocker.reset?.();
    }, [closeModal, blocker]);

    // oxlint-disable-next-line react-doctor/no-effect-event-handler -- driven by react-router blocker state (async navigation interception), not a direct user event
    useEffect(() => {
        if (isBlocked) {
            openModal(
                <ConfirmationModal
                    titleId="sections.leave.title"
                    content={t('sections.leave.body')}
                    confirmId="sections.leave.confirm"
                    cancelId="sections.leave.cancel"
                    onConfirm={handleProceed}
                    onCancel={handleCancel}
                />,
                handleCancel
            );
        }
    }, [isBlocked, handleProceed, handleCancel, openModal]);

    return null;
};

export default SectionUnsavedChangesGuard;
